import * as React from 'react';
import { isEqual, throttle } from 'lodash';
import './index.css';
import TrackUtils, { EVENT_TYPE } from '../../utils/TrackUtils';

const SLCommon = Mach.requireModule('SLCommon');

export const DietarySourceType = {
  ALLERGEN: 'allergen',
  NUTRITION: 'nutrition'
};

interface IProps {
  data?: any;
  source?: number; // 用于获取cid
  shopId?: string;
  spuId?: number;
  skuId?: number;
  groupSkuId?: number;
  maxWidth: number;
  onReportedMV?: Function; // 上报MV
  reportMVTypes?: { // 是否已经上报MV（因切换SKU不再重新上报，故需记录)
    [key: string]: boolean;
  };
  onClickTagIcon?: (params: any) => void;
}

interface IState {
  matrixTagList: any[];
}

enum TagStyle {
  /** 纯文本 */
  TextOnly = 1,
  /** 文字+图片 */
  TextWithImage,
  /** 图片+文字 */
  ImageWithText,
  /** 分割线 */
  Divider
}

export class DietaryTagView extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);
    const matrixTagList = this.processData(props.data);
    this.state = {
      matrixTagList
    };
  }

  componentWillReceiveProps(nextProps: Readonly<IProps>): void {
    const { data } = this.props;
    const dataChanged = nextProps.data && !isEqual(data, nextProps.data);
    if (dataChanged) {
      const matrixTagList = this.processData(nextProps.data);
      this.setState({
        matrixTagList
      });
    }
  }

  /**
   * 处理数据
   * */
  processData = (data) => {
    if (!data) {
      return [];
    }
    const { maxWidth } = this.props;
    const {
      burnoutDurationInfo,
      nutritionInfo,
      allergiesInfo,
      caffeineInfo,
      sodiumInfo,
      calorie
    } = data;

    const tagList = [
      calorie && {
        title: calorie,
        style: TagStyle.TextOnly,
        iconSize: 0,
        spaceWidth: 0,
        type: 'calorie'
      },
      burnoutDurationInfo?.title && {
        ...burnoutDurationInfo,
        style: TagStyle.ImageWithText,
        iconSize: 12,
        spaceWidth: 2
      },
      nutritionInfo?.title && {
        ...nutritionInfo,
        icon: 'assets://shop/dietary_info.png',
        style: TagStyle.TextWithImage,
        iconSize: 12,
        spaceWidth: 3,
        type: 'nutrition'
      },
      allergiesInfo?.title && {
        ...allergiesInfo,
        icon: 'assets://shop/dietary_info.png',
        style: TagStyle.TextWithImage,
        iconSize: 12,
        spaceWidth: 3,
        type: 'allergen'
      },
      caffeineInfo?.title && {
        ...caffeineInfo,
        style: TagStyle.ImageWithText,
        iconSize: 12,
        spaceWidth: 2
      },
      sodiumInfo?.title && {
        ...sodiumInfo,
        style: TagStyle.ImageWithText,
        iconSize: 12,
        spaceWidth: 2
      }
    ].filter(Boolean) as Array<{
      title: string;
      style: TagStyle;
      iconSize: number;
      spaceWidth: number;
    }>;

    const matrixTagList: any[] = [];
    // 当前行宽度
    let currentLineWidth = 0;
    // 分割线
    const dividerWidth = 17;
    tagList?.forEach((item) => {
      const { title = '', iconSize = 0, spaceWidth = 0 } = item || {};
      const titleWidth = this.caculateTextWidth(title);
      const totalWidth = titleWidth + iconSize + spaceWidth;
      // 获取最后一行标签数组
      let lastLineTagList: any = matrixTagList?.[matrixTagList.length - 1];
      if (!lastLineTagList) {
        lastLineTagList = [];
        matrixTagList.push(lastLineTagList);
      }
      const targetLineWidth = currentLineWidth + totalWidth + dividerWidth;
      // 判断是否超过最大宽度则新起一行展示标签
      if (targetLineWidth > maxWidth) {
        matrixTagList.push([
          item
        ]);
        currentLineWidth = totalWidth + dividerWidth;
      } else {
        currentLineWidth = targetLineWidth;
        lastLineTagList.push(item);
      }
    });
    return matrixTagList;
  }

  /**
     * 在每个元素中间插入一个指定的元素
     * @param {Array} array - 原始数组
     * @param {any} element - 要插入的元素
     * @returns {Array} - 插入元素后的新数组
     */
  insertElementBetween = (array, element) => array.reduce((acc, curr, index) => {
    if (index < array.length - 1) {
      return [...acc, curr, element];
    } else {
      return [...acc, curr];
    }
  }, [])

  /**
   * 计算文本宽度
   * */
  caculateTextWidth = (content) => {
    const width = SLCommon.widthForText({
      fontFamily: 'KeeTa-Regular',
      content,
      fontSize: 12,
      bold: 1,
    });
    return Number(width);
  }

  /**
   * 渲染分割线
   * */
  renderDivider = () => (
    <view className="tag-container">
      <view className="divider" />
    </view>
  )

  /**
   * 渲染文字+图片标签
   * */
  renderTextImageTag = (item) => {
    const {
      title, icon, spaceWidth = 0
    } = item || {};
    return (
      <view className="tag-container">
        <text className="tag-title" content={title} />
        <view
          ref={(ref) => {
            item.imageRef = ref;
          }}
          hitSlop={{
            top: 5,
            bottom: 5,
            left: 5,
            right: 5
          }}
          onClick={() => {
            this.handleClickTextImageIcon(item);
          }}
        >
          <image
            className="tag-icon"
            style={{
              marginLeft: spaceWidth,
            }}
            src={icon}
            autoRTL
          />
        </view>
      </view>
    );
  }

  /**
   * 点击文字图片标签
   * */
  handleClickTextImageIcon = throttle((item) => {
    let iconCenterX = 0;
    let iconCenterY = 0;
    if (item.imageRef) {
      const frame: { x: number; y: number; width: number; height: number; } = item.imageRef?.measureInWindow?.();
      iconCenterX = frame.x + frame.width / 2 + 1; // icon中心点位置x
      iconCenterY = frame.y + frame.height / 2; // icon中心点位置y
    }

    const { groupSkuId } = this.props;
    const params = {
      isSpread: true,
      iconCenterX,
      iconCenterY,
      type: item.type,
      groupSkuId,
    };

    const { onClickTagIcon } = this.props;
    onClickTagIcon?.(params);

    // 埋点
    this.reportTagMC(item);
  }, 1000, { leading: true, trailing: false });

  /**
   * 获取需要上报的标签类型
   * */
  getLxReportTagTypeList = () => [
    'calorie',
    'nutrition',
    'allergen'
  ]

  /**
   * 上报文字图片标签MV
   * */
  reportTagMV = (item) => {
    // 判断是否在上报列表里
    const reportLxTagTypeList = this.getLxReportTagTypeList();
    if (!reportLxTagTypeList.includes(item?.type)) {
      return;
    }

    const {
      source = 0, shopId, spuId, skuId, onReportedMV, reportMVTypes
    } = this.props;
    // 已经上报过 不再上报
    if (reportMVTypes && reportMVTypes[item?.type]) {
      return;
    }
    const params = {
      source,
      shop_id: shopId || '-999',
      spu_id: spuId || -999,
      sku_id: skuId || -999,
      spu_property_type: item?.type,
    };
    TrackUtils.reportNutritionAllergen(EVENT_TYPE.MV, params);
    onReportedMV && onReportedMV(item?.type);
  }

  /**
   * 上报文字图片标签MC
   * */
  reportTagMC = (item) => {
    // MV 营养成分/过敏源曝光事件 (选项组不上报)
    const {
      source = 0, shopId, spuId, skuId
    } = this.props;
    const params = {
      source,
      shop_id: shopId || '',
      spu_id: spuId || -999,
      sku_id: skuId || -999,
      spu_property_type: item?.type,
    };
    TrackUtils.reportNutritionAllergen(EVENT_TYPE.MC, params);
  }

  /**
   * 渲染图片+文字标签
   * */
  renderImageTextTag = (item) => {
    const {
      title, icon, spaceWidth = 0
    } = item || {};
    return (
      <view className="tag-container">
        <image
          className="tag-icon"
          style={{
            marginRight: spaceWidth
          }}
          src={icon}
          autoRTL
        />
        <text className="tag-title" content={title} />
      </view>
    );
  }

  /**
   * 渲染纯文本标签
   * */
  renderTextOnlyTag = (item) => {
    const {
      title
    } = item || {};
    return (
      <view className="tag-container">
        <text className="tag-title" content={title} />
      </view>
    );
  }

  /**
   * 渲染单个标签
   * */
  renderTagView = (item) => {
    const {
      style
    } = item || {};
    if (style === TagStyle.Divider) {
      return this.renderDivider();
    } else if (style === TagStyle.TextWithImage) {
      return this.renderTextImageTag(item);
    } else if (style === TagStyle.ImageWithText) {
      return this.renderImageTextTag(item);
    }
    return this.renderTextOnlyTag(item);
  }

  /**
   * 渲染行标签
   * */
  renderLineTagView = (tagList, index) => {
    const tagArr = this.insertElementBetween(tagList, {
      style: TagStyle.Divider
    });
    return (
      <view
        className="line-tag-container"
        style={{
          marginTop: index > 0 ? 2 : 0
        }}
      >
        {tagArr?.map((item) => (
          <view
            onLayout={() => {
              this.reportTagMV(item);
            }}
          >
            {this.renderTagView(item)}
          </view>
        ))}
      </view>
    );
  }

  render(): React.ReactNode {
    const { matrixTagList } = this.state;
    return (
      <view className="container">
        {matrixTagList?.map((tagList, index) => this.renderLineTagView(tagList, index))}
      </view>
    );
  }
}
